import DataTable from "datatables.net-bs5";
import * as Highcharts from "highcharts";
import 'highcharts/modules/heatmap';
import { getChartTheme } from './utils/chartTheme';
import * as bootstrap from "bootstrap";
// v12: heatmap self-registers on side-effect import; adds Highcharts.seriesTypes['heatmap'].
// MUST be at module level (not inside init()) so rspack bundles it before any chart call.
// (feedback_highcharts_events_nesting_cast_masks: import * as Highcharts MUST precede the module import)
if (typeof (Highcharts as any).seriesTypes?.['heatmap'] === 'undefined') {
    console.error('[heatmap] module failed to register — hour×weekday matrix will not work.');
}

export default class {
    static init = () => {
        lastfm_scrobbles();
        lastfm_trend_chart();
        lastfm_heatmap_matrix();
        lastfm_calendar();
        lastfm_topn_toggle();
    }
}

function lastfm_scrobbles(): void {
    const table = document.querySelector('[data-table="lastfm_scrobbles"]') as HTMLTableElement | null;
    if (!table) return;

    new DataTable(table, {
        serverSide: true,
        processing: true,
        ajax: {
            url: window.location.href,
            data: (d: object) => ({ ...d, do: 'getScrobbles' }),
        },
        columns: [
            // col 0: track — raw HTML anchor (<a href=...>); do NOT add render:text() or it escapes the <a>
            { data: 'track',     orderable: true,  searchable: true },
            // col 1: artist — raw HTML anchor
            { data: 'artist',    orderable: true,  searchable: true },
            // col 2: album — plain string (em-dash for null/empty, built in the model)
            { data: 'album',     orderable: true,  searchable: true },
            // col 3: scrobbled — not searchable (datetime string; filter by track/artist/album instead)
            { data: 'scrobbled', orderable: true,  searchable: false },
        ],
        order: [[3, 'desc']], // default: scrobbled_at DESC (most recently scrobbled first — D-05)
        pageLength: 25,
        lengthMenu: [10, 25, 50, 100],
    });
}

// Declare JS vars injected by the Latte {block page_js} — listed here so TS knows about them.
declare var lastfm_trend: { yw: number; week_start: string; scrobble_count: number }[];
declare var lastfm_matrix: [number, number, number][];
declare var lastfm_heatmap_year: number;
declare var lastfm_heatmap_data: Record<string, number>;

/**
 * L-2 — Listening-rate trend area chart (scrobbles per week).
 * week_start ('YYYY-MM-DD') is converted to a Unix ms timestamp for the datetime x-axis.
 * Global useUTC:false is set in src/index.ts — do NOT override per-chart.
 */
function lastfm_trend_chart(): void {
    const el = document.getElementById('lastfm-trend-chart');
    if (!el) return;
    // NOTE: read the GLOBAL data array `lastfm_trend` here. The render function MUST NOT
    // be named `lastfm_trend` too — a same-name function shadows the global in module scope,
    // making `lastfm_trend.length` the function arity (0) and silently blanking the chart.
    if (typeof lastfm_trend === 'undefined' || lastfm_trend.length === 0) return;

    const points: [number, number][] = lastfm_trend.map(row => [
        new Date(row.week_start).getTime(),
        row.scrobble_count,
    ]);

    // Phase 181: getChartTheme() supplies all chrome. D-02: accent series reads --ksat-accent at construction.
    // container id 'lastfm-trend-chart' is the DOM element id; updateAccentTaggedCharts in chartTheme.ts
    // matches this id to live-recolor series[0].color on accent picker change.
    const trendAccentHex = getComputedStyle(document.documentElement).getPropertyValue('--ksat-accent').trim();
    Highcharts.chart('lastfm-trend-chart', Highcharts.merge(getChartTheme(), {
        chart: {
            type: 'area',
            backgroundColor: 'transparent',
        },
        title: { text: '' },
        xAxis: {
            type: 'datetime',
            // labels.style.color, lineColor, tickColor come from getChartTheme()
        },
        yAxis: {
            title: { text: 'Scrobbles/week' },
            // labels.style.color, gridLineColor come from getChartTheme()
        },
        series: [{
            type: 'area',
            name: 'Scrobbles',
            data: points,
            color: trendAccentHex,  // D-02: reads live --ksat-accent at construction
            fillOpacity: 0.15,
            lineWidth: 2,
        }],
        tooltip: {
            // backgroundColor, style.color come from getChartTheme()
            formatter: function (this: any) {
                return '<b>' + Highcharts.dateFormat('%b %e, %Y', this.x) + '</b><br/>' + this.y + ' scrobbles';
            },
        },
        legend: { enabled: false },
        credits: { enabled: false },
    } as any));
}

/**
 * L-4 — Hour×weekday matrix using Highcharts heatmap module (imported at module level above).
 * Data: [[toronto_hour(0-23), dow(0-6), count], …] — Toronto-adjusted hours from PHP.
 * sentinel guard mirrors transactions.ts drilldown pattern.
 */
function lastfm_heatmap_matrix(): void {
    const el = document.getElementById('lastfm-heatmap-matrix');
    if (!el) return;
    if (typeof lastfm_matrix === 'undefined' || lastfm_matrix.length === 0) return;

    // Sentinel: heatmap module must be registered (Pitfall 6 / Plan 01 guard at module top).
    if (typeof (Highcharts as any).seriesTypes?.['heatmap'] === 'undefined') {
        console.error('[lastfm_heatmap_matrix] heatmap series type not registered — aborting chart init');
        return;
    }

    const hours = ['12am','1am','2am','3am','4am','5am','6am','7am','8am','9am','10am','11am',
                   '12pm','1pm','2pm','3pm','4pm','5pm','6pm','7pm','8pm','9pm','10pm','11pm'];
    const days  = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

    // Phase 181: getChartTheme() supplies axis/tooltip chrome. D-02: colorAxis.maxColor reads --ksat-accent
    // at construction time. container id 'lastfm-heatmap-matrix' matched by updateAccentTaggedCharts
    // for chart.update({ colorAxis: { maxColor: hex } }) on picker change (CHRT-03 safe — colorAxis not
    // in propsRequireUpdateSeries, per RESEARCH Pattern 4).
    const heatmapAccentHex = getComputedStyle(document.documentElement).getPropertyValue('--ksat-accent').trim();
    Highcharts.chart('lastfm-heatmap-matrix', Highcharts.merge(getChartTheme(), {
        chart: {
            type: 'heatmap',
            backgroundColor: 'transparent',
            height: 260,
        },
        title: { text: '' },
        xAxis: {
            categories: hours,
            labels: { style: { fontSize: '10px' } },  // color comes from getChartTheme()
            // lineColor, tickColor come from getChartTheme()
        },
        yAxis: {
            categories: days,
            reversed: true,
            title: { text: '' },
            labels: { style: { fontSize: '11px' } },  // color comes from getChartTheme()
            // gridLineColor comes from getChartTheme()
        },
        colorAxis: {
            min: 0,
            minColor: '#1a1a2e',
            maxColor: heatmapAccentHex,  // D-02: reads live --ksat-accent at construction
        },
        legend: { enabled: false },
        tooltip: {
            // backgroundColor, style.color come from getChartTheme()
            formatter: function (this: any) {
                return `<b>${days[this.point.y]}</b> at <b>${hours[this.point.x]}</b><br/>${this.point.value} scrobbles`;
            },
        },
        series: [{
            type: 'heatmap',
            name: 'Scrobbles',
            data: lastfm_matrix,
            dataLabels: { enabled: false },
        }],
        credits: { enabled: false },
    } as any));
}

/**
 * L-3 — CSS-grid calendar heatmap.
 * Algorithm cloned inline from home.ts heatmaps() lines 836-901 + interpolateColor() lines 903-913.
 * Uses a cream (#e8d4a4) → amber (#D89702) palette (read from data-color-* attrs).
 * DO NOT modify home.ts — shared + health-domain coupled (RESEARCH Assumption A3).
 */
function lastfm_calendar(): void {
    if (typeof lastfm_heatmap_data === 'undefined') return;
    const container = document.getElementById('lastfm-scrobble-heatmap');
    if (!container) return;

    const colorLight = container.dataset.colorLight || '#e8d4a4';
    const colorDark  = container.dataset.colorDark  || '#D89702';
    const year       = typeof lastfm_heatmap_year !== 'undefined' ? lastfm_heatmap_year : new Date().getFullYear();
    const data       = lastfm_heatmap_data || {};

    const values = Object.values(data) as number[];
    const maxVal = values.length > 0 ? Math.max(...values) : 1;

    const grid = document.createElement('div');
    grid.style.display              = 'grid';
    grid.style.gridTemplateColumns  = 'repeat(53, 1fr)';
    grid.style.gridTemplateRows     = 'repeat(7, 1fr)';
    grid.style.gap                  = '2px';
    grid.style.width                = '100%';
    grid.style.aspectRatio          = '53 / 7';

    const startDate = new Date(year, 0, 1);
    const startDow  = (startDate.getDay() + 6) % 7; // Monday = 0

    for (let week = 0; week < 53; week++) {
        for (let dow = 0; dow < 7; dow++) {
            const dayOffset = week * 7 + dow - startDow;
            const date = new Date(year, 0, 1 + dayOffset);
            const cell = document.createElement('div');
            cell.style.borderRadius = '2px';
            cell.style.gridColumn   = (week + 1).toString();
            cell.style.gridRow      = (dow + 1).toString();

            if (date.getFullYear() !== year) {
                cell.style.backgroundColor = 'transparent';
            } else {
                const dateStr = date.getFullYear() + '-'
                    + String(date.getMonth() + 1).padStart(2, '0') + '-'
                    + String(date.getDate()).padStart(2, '0');
                const val = data[dateStr] || 0;

                if (val === 0) {
                    // Phase 181 Pitfall 7: CSS custom-property string resolves live at paint from the
                    // current [data-bs-theme] — follows the theme without a JS re-theme loop.
                    // Same treatment as home.ts:883 (plan 04 Task 3).
                    cell.style.backgroundColor = 'var(--ksat-surface-raised)'; // empty cell — token-driven
                } else {
                    const intensity = Math.min(val / maxVal, 1);
                    cell.style.backgroundColor = lfmInterpolateColor(colorLight, colorDark, intensity);
                }

                cell.dataset.bsToggle = 'tooltip';
                cell.dataset.bsTitle  = dateStr + ': ' + val + ' scrobbles';
            }

            grid.appendChild(cell);
        }
    }

    container.appendChild(grid);

    // Initialise Bootstrap tooltips for all newly-created cells.
    container.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => {
        new bootstrap.Tooltip(el);
    });
}

/** Color interpolation helper for lastfm_calendar() — cloned from home.ts lines 903-913. */
function lfmInterpolateColor(light: string, dark: string, t: number): string {
    const parse = (hex: string) => {
        hex = hex.replace('#', '');
        return [parseInt(hex.slice(0,2), 16), parseInt(hex.slice(2,4), 16), parseInt(hex.slice(4,6), 16)];
    };
    const [r1, g1, b1] = parse(light);
    const [r2, g2, b2] = parse(dark);
    const r = Math.round(r1 + (r2 - r1) * t);
    const g = Math.round(g1 + (g2 - g1) * t);
    const b = Math.round(b1 + (b2 - b1) * t);
    return `rgb(${r},${g},${b})`;
}

/**
 * L-1 — Read-only view toggle for Top Artists & Albums (All Time ↔ This Year).
 * Shows .lastfm-top-set elements whose data-show matches the clicked button's data-scope.
 * aria-pressed toggled for accessibility.
 */
function lastfm_topn_toggle(): void {
    const buttons = document.querySelectorAll('[data-scope]');
    if (buttons.length === 0) return;

    buttons.forEach(btn => {
        btn.addEventListener('click', () => {
            const scope = (btn as HTMLElement).dataset.scope;

            // Toggle aria-pressed on all buttons in the group
            buttons.forEach(b => {
                const isActive = (b as HTMLElement).dataset.scope === scope;
                b.classList.toggle('btn-primary', isActive);
                b.classList.toggle('btn-outline-secondary', !isActive);
                b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            });

            // Show matching sets, hide others
            document.querySelectorAll<HTMLElement>('.lastfm-top-set').forEach(set => {
                set.hidden = set.dataset.show !== scope;
            });
        });
    });
}
